/**
 * Maps raw SuperfakturaDocumentsTab data (LinkedDocument[], contracts[]) for a single
 * selected quote into DealDocumentTimelineProps.
 *
 * Reuses the same relation helpers as buildHierarchyRows / documentLifecycle.
 */

import {
  isDocumentRelatedToDocument,
  isDocumentRelatedToQuote,
} from "@shared/document-relations";
import { documentLifecycle } from "@shared/document-lifecycle";
import type {
  DealDocumentTimelineProps,
  TimelineOffer,
  TimelineZalohovaFaktura,
  TimelineZmluva,
  TimelineDanovyDoklad,
  TimelineKonecnaFaktura,
  TimelineOdovzdavaciProtokol,
} from "./DealDocumentTimeline";

/** Minimal shape for a Superfaktura document row (subset of LinkedDocument). */
export interface MappableDocument {
  id: string;
  document_type?: string | null;
  document_number?: string | null;
  document_name?: string | null;
  sf_document_id?: string | null;
  related_document_id?: string | null;
  related_document_number?: string | null;
  document_date?: string | null;
  due_date?: string | null;
  total_amount?: number | null;
  subtotal_amount?: number | null;
  currency?: string | null;
  status?: string | null;
  status_id?: number | string | null;
  pdf_url?: string | null;
  raw_data?: any;
  extracted_data?: any;
  created_at?: string | null;
  generated_at?: string | null;
}

/** Minimal shape for a contract row. */
export interface MappableContract {
  id: string;
  template_id?: string | null;
  template_name?: string | null;
  pdf_filename?: string | null;
  document_ids?: string[] | null;
  generated_at?: string | null;
  created_at?: string | null;
}

// ─── Status helpers ──────────────────────────────────────────────────────────

function isQuoteAccepted(doc: MappableDocument): boolean {
  const status = (doc.status ?? "").toLowerCase();
  const raw = doc.raw_data as any;
  const rawStatusRaw =
    raw?.Invoice?.status ?? raw?.Quote?.status ?? raw?.status ?? "";
  const rawStatus = String(rawStatusRaw).toLowerCase();
  const statusId =
    doc.status_id ?? raw?.Invoice?.status_id ?? raw?.Quote?.status_id;

  return (
    status === "approved" ||
    status === "schválená" ||
    status === "schvalena" ||
    status === "accepted" ||
    status === "confirmed" ||
    rawStatus === "approved" ||
    rawStatus === "schválená" ||
    rawStatus === "schvalena" ||
    rawStatus === "accepted" ||
    rawStatus === "confirmed" ||
    rawStatus === "2" ||
    statusId === 2 ||
    statusId === 3 ||
    String(statusId) === "2" ||
    String(statusId) === "3" ||
    String(rawStatusRaw) === "2" ||
    String(rawStatusRaw) === "3"
  );
}

function isQuoteDeclined(doc: MappableDocument): boolean {
  const status = (doc.status ?? "").toLowerCase();
  const raw = doc.raw_data as any;
  const rawStatus = String(
    raw?.Invoice?.status ?? raw?.Quote?.status ?? raw?.status ?? "",
  ).toLowerCase();
  return (
    status === "declined" ||
    status === "rejected" ||
    status === "cancelled" ||
    status === "zamietnutá" ||
    rawStatus === "declined" ||
    rawStatus === "rejected" ||
    rawStatus === "cancelled" ||
    rawStatus === "zamietnutá"
  );
}

function isDepositPaid(doc: MappableDocument): boolean {
  const status = (doc.status ?? "").toLowerCase();
  const raw = doc.raw_data as any;
  const rawStatus = String(
    raw?.Invoice?.status ?? raw?.status ?? "",
  ).toLowerCase();
  return (
    status === "paid" ||
    status === "uhradená" ||
    status === "uhradena" ||
    rawStatus === "paid" ||
    rawStatus === "uhradená" ||
    rawStatus === "uhradena"
  );
}

function isInvoicePaid(doc: MappableDocument): boolean {
  return isDepositPaid(doc);
}

function docAmount(doc: MappableDocument): number {
  const fin = doc.extracted_data?.financial as
    | { total?: unknown; subtotal?: unknown }
    | undefined;
  if (fin?.total != null) {
    const n = Number(fin.total);
    if (Number.isFinite(n)) return n;
  }
  if (doc.total_amount != null) {
    const n = Number(doc.total_amount);
    if (Number.isFinite(n)) return n;
  }
  if (doc.subtotal_amount != null) {
    const n = Number(doc.subtotal_amount);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

const PROTOCOL_ID = "odovzdavaci-protokol";
const ZMLUVA_ID = "zmluva-o-dielo";

function isProtocol(c: MappableContract): boolean {
  if (c.template_id === PROTOCOL_ID) return true;
  const name = (c.template_name ?? "").toLowerCase();
  return name.includes("odovzd") || name.includes("preber") || name.includes("protokol");
}

function isZmluva(c: MappableContract): boolean {
  if (c.template_id === ZMLUVA_ID) return true;
  const name = (c.template_name ?? "").toLowerCase();
  return name.includes("zmluva");
}

// ─── Main mapper ─────────────────────────────────────────────────────────────

export interface MapTimelineResult
  extends Omit<
    DealDocumentTimelineProps,
    | "onDocumentOpen"
    | "onDocumentDownload"
    | "onDocumentEmail"
    | "onDocumentDelete"
    | "onOfferDuplicate"
  > {}

/**
 * Build DealDocumentTimelineProps data (without callbacks) for the given quote
 * from all deal documents and contracts.
 */
export function mapTimelineData(
  quoteDoc: MappableDocument,
  allDocs: MappableDocument[],
  allContracts: MappableContract[],
): MapTimelineResult {
  const quoteId = quoteDoc.id;
  const quoteNum = quoteDoc.document_number ?? quoteDoc.sf_document_id;
  const quoteSf = quoteDoc.sf_document_id;

  // ── Offers (all quotes for this deal, not just the selected one) ─────────
  const allQuotes = allDocs.filter((d) => d.document_type === "quote");
  const offers: TimelineOffer[] = allQuotes
    .slice()
    .sort((a, b) => {
      const byDate =
        new Date(b.document_date ?? 0).getTime() -
        new Date(a.document_date ?? 0).getTime();
      if (byDate !== 0) return byDate;
      return (b.document_number ?? "").localeCompare(a.document_number ?? "");
    })
    .map((q): TimelineOffer => {
      const status: TimelineOffer["status"] = isQuoteAccepted(q)
        ? "accepted"
        : isQuoteDeclined(q)
          ? "declined"
          : "open";
      const number =
        q.document_number ??
        q.document_name ??
        q.sf_document_id ??
        q.id;
      const description =
        q.document_name && q.document_name !== number ? q.document_name : undefined;
      return {
        id: q.id,
        number,
        description,
        date: q.document_date ?? q.created_at ?? "",
        amount: docAmount(q),
        status,
      };
    });

  // ── Find related docs for the selected quote ─────────────────────────────
  const relatedToQuote = (d: MappableDocument) =>
    d.id !== quoteId &&
    isDocumentRelatedToQuote(d as any, quoteId, quoteNum, quoteSf);

  const deposits = allDocs.filter(
    (d) =>
      (d.document_type === "deposit_invoice" || d.document_type === "proforma") &&
      relatedToQuote(d),
  );
  const deposit = deposits[0] as MappableDocument | undefined;

  // Grandchildren: docs related to deposit
  const depositChildren = deposit
    ? allDocs.filter(
        (d) =>
          d.id !== deposit.id &&
          isDocumentRelatedToDocument(d as any, deposit.id, deposit as any),
      )
    : [];

  const taxDoc =
    allDocs.find(
      (d) =>
        d.document_type === "tax_document" &&
        (relatedToQuote(d) ||
          (deposit && depositChildren.some((c) => c.id === d.id))),
    ) ??
    depositChildren.find(
      (d) => d.document_type === "tax_document" || d.document_type === "invoice",
    );

  const finalInvoice = allDocs.find(
    (d) =>
      d.document_type === "invoice" &&
      d.id !== taxDoc?.id &&
      (relatedToQuote(d) ||
        (deposit && isDocumentRelatedToDocument(d as any, deposit.id, deposit as any))),
  );

  // ── Contracts ─────────────────────────────────────────────────────────────
  const subtreeIds = new Set<string>([
    quoteId,
    ...(deposit ? [deposit.id] : []),
    ...(taxDoc ? [taxDoc.id] : []),
    ...(finalInvoice ? [finalInvoice.id] : []),
  ]);

  const isAttached = (c: MappableContract) =>
    Array.isArray(c.document_ids) &&
    c.document_ids.some((id) => subtreeIds.has(id));

  const zmluvaContract = allContracts.find(
    (c) => isAttached(c) && isZmluva(c),
  );
  const protokolContract = allContracts.find(
    (c) => isAttached(c) && isProtocol(c),
  );

  // ── Build timeline props ──────────────────────────────────────────────────
  const zalohovaFaktura: TimelineZalohovaFaktura | undefined = deposit
    ? {
        id: deposit.id,
        number:
          deposit.document_number ??
          deposit.document_name ??
          deposit.sf_document_id ??
          deposit.id,
        date: deposit.document_date ?? deposit.created_at ?? "",
        amount: docAmount(deposit),
        paid: isDepositPaid(deposit),
        generatedFromQuoteId: quoteId,
      }
    : undefined;

  const zmluva: TimelineZmluva | undefined = zmluvaContract
    ? {
        id: zmluvaContract.id,
        date:
          zmluvaContract.generated_at ??
          zmluvaContract.created_at ??
          "",
        // Treat existence of PDF contract as "signed" until a dedicated field exists
        signed: true,
      }
    : undefined;

  const danovyDoklad: TimelineDanovyDoklad | undefined = taxDoc
    ? {
        id: taxDoc.id,
        number:
          taxDoc.document_number ??
          taxDoc.document_name ??
          taxDoc.sf_document_id ??
          taxDoc.id,
        date: taxDoc.document_date ?? taxDoc.created_at ?? "",
        amount: docAmount(taxDoc),
      }
    : undefined;

  const konecnaFaktura: TimelineKonecnaFaktura | undefined = finalInvoice
    ? {
        id: finalInvoice.id,
        number:
          finalInvoice.document_number ??
          finalInvoice.document_name ??
          finalInvoice.sf_document_id ??
          finalInvoice.id,
        date: finalInvoice.document_date ?? finalInvoice.created_at ?? "",
        amount: docAmount(finalInvoice),
      }
    : undefined;

  const odovzdavaciProtokol: TimelineOdovzdavaciProtokol | undefined =
    protokolContract
      ? {
          id: protokolContract.id,
          date:
            protokolContract.generated_at ??
            protokolContract.created_at ??
            "",
        }
      : undefined;

  return {
    offers,
    zalohovaFaktura,
    zmluva,
    danovyDoklad,
    konecnaFaktura,
    odovzdavaciProtokol,
  };
}

/** Deposit or proforma linked to this quote. */
export function quoteHasDepositInvoice(
  q: MappableDocument,
  allDocs: MappableDocument[],
): boolean {
  const lid = q.id;
  const num = q.document_number ?? q.sf_document_id;
  const sf = q.sf_document_id;
  return allDocs.some(
    (d) =>
      (d.document_type === "deposit_invoice" || d.document_type === "proforma") &&
      isDocumentRelatedToQuote(d as any, lid, num, sf),
  );
}

/** Any downstream SF doc (záloha, daňový, faktúra) tied to the quote. */
export function quoteHasLinkedChainDocuments(
  q: MappableDocument,
  allDocs: MappableDocument[],
): boolean {
  const lid = q.id;
  const num = q.document_number ?? q.sf_document_id;
  const sf = q.sf_document_id;
  return allDocs.some(
    (d) =>
      d.id !== lid &&
      (d.document_type === "deposit_invoice" ||
        d.document_type === "proforma" ||
        d.document_type === "tax_document" ||
        d.document_type === "invoice") &&
      isDocumentRelatedToQuote(d as any, lid, num, sf),
  );
}

export function listQuotesWithoutDeposit(
  quotes: MappableDocument[],
  allDocs: MappableDocument[],
): MappableDocument[] {
  return quotes.filter((q) => !quoteHasDepositInvoice(q, allDocs));
}

function lifecycleChainScore(
  q: MappableDocument,
  allDocs: MappableDocument[],
  contracts: MappableContract[],
): number {
  const life = documentLifecycle(q as any, allDocs as any, contracts as any);
  return (
    (life.hasProtocol ? 32 : 0) +
    (life.hasFinal ? 16 : 0) +
    (life.hasTaxDoc ? 8 : 0) +
    (life.hasContract ? 4 : 0) +
    (life.hasDeposit ? 2 : 0)
  );
}

/**
 * Pick which quote's document chain the timeline should show.
 * Prefers the quote that already has linked documents; if several, the most advanced lifecycle wins.
 * Otherwise a single Schválená quote, else the first in the list (newest-first sort recommended).
 */
export function resolveTimelineActiveQuoteId(
  quotesSortedNewestFirst: MappableDocument[],
  allDocs: MappableDocument[],
  contracts: MappableContract[],
): string | null {
  if (quotesSortedNewestFirst.length === 0) return null;
  if (quotesSortedNewestFirst.length === 1) return quotesSortedNewestFirst[0].id;

  const withChain = quotesSortedNewestFirst.filter((q) =>
    quoteHasLinkedChainDocuments(q, allDocs),
  );

  if (withChain.length === 1) return withChain[0].id;

  if (withChain.length > 1) {
    let best = withChain[0];
    let bestScore = -1;
    for (const q of withChain) {
      const s = lifecycleChainScore(q, allDocs, contracts);
      if (s > bestScore) {
        bestScore = s;
        best = q;
      }
    }
    return best.id;
  }

  const approvedOnly = quotesSortedNewestFirst.filter((q) => isQuoteAccepted(q));
  if (approvedOnly.length === 1) return approvedOnly[0].id;

  return quotesSortedNewestFirst[0].id;
}
